import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {Subscription} from 'rxjs';
import {OrganisationDetail} from '@shared/api/models/query/organisation.model';

export const ORGANISATION_NAME_MAX_LENGTH = 100;
/** Longest code the backend accepts for a new or edited organisation. */
export const ORGANISATION_CODE_MAX_LENGTH = 10;
export const ORGANISATION_ADDRESS_MAX_LENGTH = 100;
export const ORGANISATION_CITY_MAX_LENGTH = 50;
export const ORGANISATION_POSTCODE_MAX_LENGTH = 20;
/** Fixed start of every code created on the New organisation page. */
export const ORGANISATION_CODE_PREFIX = 'ORG-';

/** Value emitted by the form's `save` output. Empty optional fields are null. */
export interface OrganisationFormValue {
    name: string,
    /** The full code: the prefix (when the form has one) followed by what was typed. */
    code: string,
    address: string | null,
    city: string | null,
    postcode: string | null,
}

type OrganisationFormField = 'name' | 'code' | 'address' | 'city' | 'postcode';

/** Whitespace-only counts as empty (same rule as the contract form). */
const trimmedRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim() === '' ? {required: true} : null;

/**
 * UNIVAPLAT-3692: shared organisation form, used by the New organisation page and (UNIVAPLAT-3693)
 * the detail General tab. Presentational: it never calls the API. With `codePrefix` set (New page,
 * and the detail page for codes that start with it) the Code field is a disabled prefix plus an
 * input for the rest, and the emitted code is the prefix followed by that input. Without it
 * (legacy codes with another start) Code is one free input. Form content is
 * never logged.
 */
@Component({
    selector: 'cdss-organisation-form',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './organisation-form.component.html',
    styleUrls: ['./organisation-form.component.scss']
})
export class OrganisationFormComponent implements OnChanges, OnDestroy {
    readonly nameMaxLength = ORGANISATION_NAME_MAX_LENGTH;
    readonly addressMaxLength = ORGANISATION_ADDRESS_MAX_LENGTH;
    readonly cityMaxLength = ORGANISATION_CITY_MAX_LENGTH;
    readonly postcodeMaxLength = ORGANISATION_POSTCODE_MAX_LENGTH;

    /** Organisation to pre-fill the form with (edit); empty form when absent. */
    @Input() organisation?: OrganisationDetail | null;
    /** No Save button and no editable field. */
    @Input() readOnly = false;
    /** Code field not editable while the others are (edit by an organisation admin). */
    @Input() codeReadOnly = false;
    /** Fixed code prefix shown disabled before the Code input (New page); none when absent. */
    @Input() codePrefix: string | null = null;
    /** A request is in flight: Save is disabled and announced as busy. */
    @Input() saving = false;
    /** Backend text for a duplicate code, shown on the Code field until the code is edited. */
    @Input() codeError: string | null = null;
    @Input() idPrefix = 'organisation';

    @Output() save = new EventEmitter<OrganisationFormValue>();

    readonly form = new FormGroup({
        name: new FormControl<string>('', {
            nonNullable: true,
            validators: [trimmedRequired, Validators.maxLength(ORGANISATION_NAME_MAX_LENGTH)]
        }),
        // Maximum length depends on the prefix, so it is set in `applyCodeLimit`.
        code: new FormControl<string>('', {
            nonNullable: true,
            validators: [trimmedRequired, Validators.maxLength(ORGANISATION_CODE_MAX_LENGTH)]
        }),
        address: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.maxLength(ORGANISATION_ADDRESS_MAX_LENGTH)]
        }),
        city: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.maxLength(ORGANISATION_CITY_MAX_LENGTH)]
        }),
        postcode: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.maxLength(ORGANISATION_POSTCODE_MAX_LENGTH)]
        }),
    });

    serverCodeError: string | null = null;

    private readonly codeSub: Subscription = this.form.controls.code.valueChanges.subscribe(() => {
        this.serverCodeError = null;
    });

    /** Characters left for the Code input once the prefix is taken out of the 10. */
    get codeMaxLength(): number {
        return ORGANISATION_CODE_MAX_LENGTH - (this.codePrefix?.length ?? 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['codePrefix']) {
            this.applyCodeLimit();
        }
        if (changes['organisation']) {
            this.patchFromOrganisation();
        }
        if (changes['readOnly'] || changes['codeReadOnly'] || changes['organisation']) {
            this.applyEnabledState();
        }
        if (changes['codeError']) {
            this.serverCodeError = this.codeError || null;
            if (this.serverCodeError) {
                this.form.controls.code.setErrors({server: true});
                this.form.controls.code.markAsTouched();
            }
        }
    }

    ngOnDestroy(): void {
        this.codeSub.unsubscribe();
        // Drop what was typed: addresses can identify a person.
        this.form.reset();
    }

    get canSave(): boolean {
        return !this.saving && this.form.valid;
    }

    onSubmit(): void {
        if (this.readOnly || this.saving) {
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const raw = this.form.getRawValue();
        this.save.emit({
            name: raw.name.trim(),
            code: (this.codePrefix ?? '') + raw.code.trim(),
            address: this.emptyToNull(raw.address),
            city: this.emptyToNull(raw.city),
            postcode: this.emptyToNull(raw.postcode),
        });
    }

    /** True when the control shows an error: touched and invalid. */
    showError(name: OrganisationFormField): boolean {
        const control = this.form.controls[name];
        return control.invalid && (control.touched || control.dirty);
    }

    /** Id of the error element for a control, or null when none shown (for aria-describedby). */
    describedBy(name: OrganisationFormField): string | null {
        return this.showError(name) ? `${this.idPrefix}-${name}-error` : null;
    }

    /** The Code input is also described by the fixed prefix text, so a screen reader reads it. */
    codeDescribedBy(): string | null {
        const ids = [this.codePrefix ? `${this.idPrefix}-code-prefix` : null, this.describedBy('code')];
        return ids.filter((id): id is string => !!id).join(' ') || null;
    }

    private emptyToNull(value: string): string | null {
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
    }

    /** The prefix is shown as fixed text, so the input holds only what follows it. */
    private withoutPrefix(code: string): string {
        const prefix = this.codePrefix;
        return prefix && code.startsWith(prefix) ? code.slice(prefix.length) : code;
    }

    private applyCodeLimit(): void {
        const control = this.form.controls.code;
        control.setValidators([trimmedRequired, Validators.maxLength(this.codeMaxLength)]);
        control.updateValueAndValidity({emitEvent: false});
    }

    private patchFromOrganisation(): void {
        const organisation = this.organisation;
        this.form.reset({
            name: organisation?.name ?? '',
            code: this.withoutPrefix(organisation?.code ?? ''),
            address: organisation?.address ?? '',
            city: organisation?.city ?? '',
            postcode: organisation?.postcode ?? '',
        });
        this.serverCodeError = null;
    }

    private applyEnabledState(): void {
        if (this.readOnly) {
            this.form.disable({emitEvent: false});
            return;
        }
        this.form.enable({emitEvent: false});
        if (this.codeReadOnly) {
            this.form.controls.code.disable({emitEvent: false});
        }
    }
}
